import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { PrismBadge } from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import {
  COLUMN_PROPERTIES,
  type ColumnProperties,
} from 'lumen/components/hotel/settings/central-mapping/tables/table-mapping';
import SettingsCentralMappingDropdownsDropdownBarBased from 'lumen/components/settings/central-mapping/dropdowns/dropdown-bar-based';
import SettingsCentralMappingDropdownsDropdownCommissionable from 'lumen/components/settings/central-mapping/dropdowns/dropdown-commissionable';
import SettingsCentralMappingDropdownsDropdownMapping from 'lumen/components/settings/central-mapping/dropdowns/dropdown-mapping';
import SettingsCentralMappingTablesCellsEditableCheckboxCell from 'lumen/components/settings/central-mapping/tables/cells/editable-checkbox-cell';
import SettingsCentralMappingTablesCellsEditableNumberCell from 'lumen/components/settings/central-mapping/tables/cells/editable-number-cell';
import SettingsCentralMappingTablesCellsEditableTextCell from 'lumen/components/settings/central-mapping/tables/cells/editable-text-cell';
import type {
  CentralMappingCategory,
  CentralMappingMappingEntryMarketSegment,
} from 'lumen/models';
import type {
  BarBased,
  Commissionable,
} from 'lumen/models/central-mapping/mapping-entry-market-segment';
import { isMappingEntryRoomType } from 'lumen/models/central-mapping/mapping-entry-room-type';
import {
  type Breakdown,
  BREAKDOWNS,
  type CentralMappingMappingEntryExtendedModel,
  DEEP_DISCOUNT_CATEGORY_NAME,
  mappingEntryHasBarBased,
  mappingEntryHasCommissionable,
  MAX_ADULTS_IN_ROOM,
  PMS_VALUE_TYPES,
  type PmsValueType,
} from 'lumen/utils/central-mapping-constants';
import { FEATURE_TOGGLES } from 'lumen/utils/constants/feature-toggle-constants';

interface HotelSettingsCentralMappingTablesTableMappingBulkEditRowSignature {
  Args: {
    barBased: {
      setBarBased: (value: BarBased) => void;
    };
    breakdown: Breakdown;
    bulkEditRow: {
      numberOfSelectedRows: number;
    };
    commissionable: {
      setCommissionable: (value: Commissionable) => void;
    };
    deepDiscountCategory?: CentralMappingCategory;
    mapping: {
      hierarchyCategories: CentralMappingCategory[];
      setCategory: (category: CentralMappingCategory | null) => void;
    };
    selectedMappingEntries: CentralMappingMappingEntryExtendedModel[];
    valueType: PmsValueType;
  };
}

export default class HotelSettingsCentralMappingTablesTableMappingBulkEditRow extends Component<HotelSettingsCentralMappingTablesTableMappingBulkEditRowSignature> {
  @service declare centralMapping: Services['central-mapping'];
  @service declare featureToggle: Services['feature-toggle'];
  @service declare usageAnalytics: Services['usage-analytics'];

  get bulkAutoBarBased(): BarBased {
    const { selectedMappingEntriesHasBarBased: selectedMappingEntries } = this;

    if (!selectedMappingEntries[0]) {
      return null;
    }

    const firstBarBased = selectedMappingEntries[0].autoBarBased;

    if (
      selectedMappingEntries.every(
        (entry) => entry.autoBarBased === firstBarBased,
      )
    ) {
      return firstBarBased;
    }

    return null;
  }

  get bulkAutoCategory(): CentralMappingCategory | null {
    const { selectedMappingEntries } = this.args;

    if (!selectedMappingEntries[0]) {
      return null;
    }

    const firstAutoCategory = selectedMappingEntries[0].autoCategory;

    if (
      selectedMappingEntries.every(
        (entry) => entry.autoCategory === firstAutoCategory,
      )
    ) {
      return firstAutoCategory;
    }

    return null;
  }

  get bulkAutoCommissionable(): Commissionable {
    const { selectedMappingEntriesHasCommissionable: selectedMappingEntries } =
      this;

    if (!selectedMappingEntries[0]) {
      return null;
    }

    const firstCommissionable = selectedMappingEntries[0].autoCommissionable;

    if (
      selectedMappingEntries.every(
        (entry) => entry.autoCommissionable === firstCommissionable,
      )
    ) {
      return firstCommissionable;
    }

    return null;
  }

  get bulkBarBased(): BarBased {
    const { selectedMappingEntriesHasBarBased: selectedMappingEntries } = this;

    if (!selectedMappingEntries[0]) {
      return null;
    }

    const firstBarBased = selectedMappingEntries[0].barBased;

    if (
      selectedMappingEntries.every((entry) => entry.barBased === firstBarBased)
    ) {
      return firstBarBased;
    }

    return null;
  }

  get bulkCategory(): CentralMappingCategory | null {
    const { selectedMappingEntries } = this.args;

    if (!selectedMappingEntries[0]) {
      return null;
    }

    const firstCategory = selectedMappingEntries[0].category;

    if (
      selectedMappingEntries.every((entry) => entry.category === firstCategory)
    ) {
      return firstCategory;
    }

    return null;
  }

  get bulkCommissionable(): Commissionable {
    const { selectedMappingEntriesHasCommissionable: selectedMappingEntries } =
      this;

    if (!selectedMappingEntries[0]) {
      return null;
    }

    const firstCommissionable = selectedMappingEntries[0].commissionable;

    if (
      selectedMappingEntries.every(
        (entry) => entry.commissionable === firstCommissionable,
      )
    ) {
      return firstCommissionable;
    }

    return null;
  }

  get bulkDeepDiscount(): { isMixed: boolean; value: boolean } {
    const { selectedMappingEntries } = this.args;

    if (!selectedMappingEntries[0]) {
      return { isMixed: false, value: false };
    }

    const isDeepDiscount = selectedMappingEntries.map(
      (entry) => entry.category?.name === DEEP_DISCOUNT_CATEGORY_NAME,
    );

    if (isDeepDiscount.every((value) => value)) {
      return { isMixed: false, value: true };
    }

    if (isDeepDiscount.every((value) => !value)) {
      return { isMixed: false, value: false };
    }

    return { isMixed: true, value: false };
  }

  get bulkUserBarBased(): BarBased {
    const { selectedMappingEntriesHasBarBased: selectedMappingEntries } = this;

    if (!selectedMappingEntries[0]) {
      return null;
    }

    // Edge case: when the barBase values are the same, but it's a mix of user-mapped and auto-mapped, the bulk edit dropdown should show the barBase as user-mapped.
    // That's why we check on `barBase` here and not `userbarBase`.
    const firstBarBased = selectedMappingEntries[0].barBased;

    if (
      selectedMappingEntries.every((entry) => entry.barBased === firstBarBased)
    ) {
      return firstBarBased;
    }

    return null;
  }

  get bulkUserCategory(): CentralMappingCategory | null {
    const { selectedMappingEntries } = this.args;

    if (!selectedMappingEntries[0]) {
      return null;
    }

    const firstUserCategory = selectedMappingEntries[0].userCategory;

    if (
      selectedMappingEntries.every(
        (entry) => entry.userCategory === firstUserCategory,
      )
    ) {
      return firstUserCategory;
    }

    return null;
  }

  get bulkUserCommissionable(): Commissionable {
    const { selectedMappingEntriesHasCommissionable: selectedMappingEntries } =
      this;

    if (!selectedMappingEntries[0]) {
      return null;
    }

    // Edge case: when the commissionable values are the same, but it's a mix of user-mapped and auto-mapped,
    // the bulk edit dropdown should show it as user-mapped. That's why we check on `commissionable` here.
    const firstCommissionable = selectedMappingEntries[0].commissionable;

    if (
      selectedMappingEntries.every(
        (entry) => entry.commissionable === firstCommissionable,
      )
    ) {
      return firstCommissionable;
    }

    return null;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get description() {
    const { selectedMappingEntries } = this.args;

    if (!selectedMappingEntries[0]) {
      return { value: '', isMixed: false };
    }

    const firstDescription = selectedMappingEntries[0].description;

    if (
      selectedMappingEntries.every(
        (entry) => entry.description === firstDescription,
      )
    ) {
      return {
        isMixed: false,
        value: firstDescription,
      };
    }

    return {
      isMixed: true,
      value: '',
    };
  }

  get isCommissionableEnabled(): boolean {
    return this.featureToggle.hasFeatureToggle(
      FEATURE_TOGGLES.NET_REVENUE_SETTINGS,
    );
  }

  get isMarketSegmentBreakdown(): boolean {
    return this.args.breakdown === BREAKDOWNS.MARKET_SEGMENT;
  }

  get isMixedBarBased(): boolean {
    const { selectedMappingEntriesHasBarBased: selectedMappingEntries } = this;

    if (!selectedMappingEntries[0]) {
      return false;
    }

    const firstBarBased = selectedMappingEntries[0].barBased;

    if (
      selectedMappingEntries.every((entry) => entry.barBased === firstBarBased)
    ) {
      return false;
    }

    return true;
  }

  get isMixedCategory(): boolean {
    const { selectedMappingEntries } = this.args;

    if (!selectedMappingEntries[0]) {
      return false;
    }

    const firstCategory = selectedMappingEntries[0].category;

    if (
      selectedMappingEntries.every((entry) => entry.category === firstCategory)
    ) {
      return false;
    }

    return true;
  }

  get isMixedCommissionable(): boolean {
    const { selectedMappingEntriesHasCommissionable: selectedMappingEntries } =
      this;

    if (!selectedMappingEntries[0]) {
      return false;
    }

    const firstCommissionable = selectedMappingEntries[0].commissionable;

    if (
      selectedMappingEntries.every(
        (entry) => entry.commissionable === firstCommissionable,
      )
    ) {
      return false;
    }

    return true;
  }

  get isRatePlanValueType(): boolean {
    return this.args.valueType === PMS_VALUE_TYPES.RATE_CODE;
  }

  get isRoomTypeBreakdown(): boolean {
    return this.args.breakdown === BREAKDOWNS.ROOM_TYPE;
  }

  get isStayRestrictionRateGroupBreakdown(): boolean {
    return this.args.breakdown === BREAKDOWNS.STAY_RESTRICTION_RATE_GROUP;
  }

  get maxAdults(): {
    isMixed?: boolean;
    value: number | null;
  } {
    const selectedMappingEntries = this.args.selectedMappingEntries.filter(
      (entry) =>
        // @ts-expect-error: Incorrect type
        isMappingEntryRoomType(entry),
    );

    if (!selectedMappingEntries[0]) {
      return {
        value: null,
      };
    }

    // The bulk value of maxAdults is the common value in all selected mapping entries.
    // If there is no common value, the value is null and the dropdown will display "Mixed".
    const firstMaxAdults = selectedMappingEntries[0].maxAdults;

    if (
      selectedMappingEntries.every(
        (entry) => entry.maxAdults === firstMaxAdults,
      )
    ) {
      return {
        value: firstMaxAdults,
      };
    }

    return {
      value: null,
      isMixed: true,
    };
  }

  // Getter used to narrow the type of CentralMappingMappingEntryExtendedModel[],
  // otherwise we can't access the `userBarBased` or `autoCategory` properties.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get selectedMappingEntriesHasBarBased() {
    return this.args.selectedMappingEntries.filter((mappingEntry) =>
      // @ts-expect-error: Incorrect type
      mappingEntryHasBarBased(mappingEntry),
    );
  }

  get selectedMappingEntriesHasCommissionable(): CentralMappingMappingEntryMarketSegment[] {
    return this.args.selectedMappingEntries.filter((mappingEntry) =>
      mappingEntryHasCommissionable(mappingEntry),
    );
  }

  get showCommissionableBulkCell(): boolean {
    return this.isRatePlanValueType && this.isCommissionableEnabled;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  trackBulkUpdate(updatedColumn: ColumnProperties) {
    const rowCount = new Set(
      this.args.selectedMappingEntries.map((entry) => {
        return entry.value;
      }),
    ).size;

    this.usageAnalytics.track('Mapping Bulk Updated', {
      eventTrigger: 'bulk-changes',
      bulk: updatedColumn,
      rowCount,
    });

    this.centralMapping.trackMappingEntryChanged({
      mappingChange: updatedColumn,
      rowCount,
      rowType: 'bulk',
    });
  }

  @action
  setBarBased(value: BarBased | null): void {
    this.args.barBased.setBarBased(value);
    this.trackBulkUpdate(COLUMN_PROPERTIES.BAR_BASED);
  }

  @action
  setCategory(category: CentralMappingCategory | null): void {
    this.args.mapping.setCategory(category);
    this.trackBulkUpdate(COLUMN_PROPERTIES.MAPPING);
  }

  @action
  setCommissionable(value: Commissionable): void {
    this.args.commissionable.setCommissionable(value);
    this.trackBulkUpdate(COLUMN_PROPERTIES.COMMISSIONABLE);
  }

  @action
  setDeepDiscount(checked: boolean): void {
    this.args.mapping.setCategory(
      checked ? (this.args.deepDiscountCategory ?? null) : null,
    );
    this.trackBulkUpdate(COLUMN_PROPERTIES.DEEP_DISCOUNT);
  }

  @action
  setDescription(value: string): void {
    this.args.selectedMappingEntries.forEach((entry) => {
      entry.description = value;
    });
    this.trackBulkUpdate(COLUMN_PROPERTIES.DESCRIPTION);
  }

  @action
  setMaxAdultsValue(value: number | null): void {
    this.args.selectedMappingEntries.forEach((entry) => {
      // @ts-expect-error: Incorrect type
      if (isMappingEntryRoomType(entry)) {
        entry.maxAdults = value;
      }
    });
    this.trackBulkUpdate(COLUMN_PROPERTIES.MAX_ADULTS);
  }

  <template>
    <th class="bulk-edit-row-cell">
      <PrismBadge
        @label={{t
          "settings.central_mapping.mapping.selected.count"
          class="text-200--bold"
          count=@bulkEditRow.numberOfSelectedRows
          htmlSafe=true
        }}
        @type="info"
      />
    </th>

    <th
      class="bulk-edit-row-cell py-0"
      data-test-id="bulk-edit-row-cell-description"
    >
      <SettingsCentralMappingTablesCellsEditableTextCell
        @cellDataTestId="bulk-action-description"
        @isMixed={{this.description.isMixed}}
        @setValue={{this.setDescription}}
        @value={{this.description.value}}
      />
    </th>

    {{#if this.isStayRestrictionRateGroupBreakdown}}
      <th class="bulk-edit-row-cell">
        <SettingsCentralMappingTablesCellsEditableCheckboxCell
          @isMixed={{this.bulkDeepDiscount.isMixed}}
          @setValue={{this.setDeepDiscount}}
          @value={{this.bulkDeepDiscount.value}}
        />
      </th>
    {{else}}
      <th class="bulk-edit-row-cell" data-test-id="bulk-edit-row-cell-mapping">
        <SettingsCentralMappingDropdownsDropdownMapping
          @autoCategory={{this.bulkAutoCategory}}
          @category={{this.bulkCategory}}
          @hideAutomappingVisuals={{true}}
          @hierarchyCategories={{@mapping.hierarchyCategories}}
          @isMixed={{this.isMixedCategory}}
          @setCategory={{this.setCategory}}
          @userCategory={{this.bulkUserCategory}}
          @variant="default"
        />
      </th>
    {{/if}}

    {{#if this.isMarketSegmentBreakdown}}
      <th
        class="bulk-edit-row-cell"
        data-test-id="bulk-edit-row-cell-bar-based"
      >
        <SettingsCentralMappingDropdownsDropdownBarBased
          @autoBarBased={{this.bulkAutoBarBased}}
          @barBased={{this.bulkBarBased}}
          @hideAutomappingVisuals={{true}}
          @isMixed={{this.isMixedBarBased}}
          @setBarBased={{this.setBarBased}}
          @userBarBased={{this.bulkUserBarBased}}
          @variant="default"
        />
      </th>

      {{#if this.showCommissionableBulkCell}}
        <th
          class="bulk-edit-row-cell"
          data-test-id="bulk-edit-row-cell-commissionable"
        >
          <SettingsCentralMappingDropdownsDropdownCommissionable
            @autoCommissionable={{this.bulkAutoCommissionable}}
            @commissionable={{this.bulkCommissionable}}
            @hideAutomappingVisuals={{true}}
            @isMixed={{this.isMixedCommissionable}}
            @setCommissionable={{this.setCommissionable}}
            @userCommissionable={{this.bulkUserCommissionable}}
            @variant="default"
          />
        </th>
      {{/if}}
    {{else if this.isRoomTypeBreakdown}}
      {{! Room type breakdown - Inventory }}
      <th class="bulk-edit-row-cell py-0" />

      {{! Room type breakdown - No. Adults }}
      <th
        class="bulk-edit-row-cell py-0"
        data-test-id="bulk-edit-row-cell-max-adults"
      >
        <SettingsCentralMappingTablesCellsEditableNumberCell
          @cellDataTestId="bulk-action-max-adults"
          @isMixed={{this.maxAdults.isMixed}}
          @maxValue={{MAX_ADULTS_IN_ROOM}}
          @placeholder={{t
            "settings.central_mapping.mapping.max_adults.placeholder"
          }}
          @setValue={{this.setMaxAdultsValue}}
          @value={{this.maxAdults.value}}
        />
      </th>
    {{/if}}
  </template>
}
